import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import { LoginInputSchema, type LoginInput } from '@sophia/shared-schemas'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Field } from '@/components/ui/Field'
import { SophiaWordmark } from '@/components/SophiaMark'
import { login } from '../api'
import { useAuthStore } from '@/store/auth.store'
import { extractApiError } from '@/lib/api'

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, setUser } = useAuthStore()
  const qc = useQueryClient()
  const [submitError, setSubmitError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(LoginInputSchema),
    defaultValues: { email: '', senha: '' },
  })

  const mutation = useMutation({
    mutationFn: login,
    onSuccess: (data) => {
      // Atualiza o cache da query 'auth/me' pra evitar que o ProtectedRoute
      // re-rode com isError=true em cache do bootstrap inicial.
      qc.setQueryData(['auth', 'me'], data.user)
      setUser(data.user)
      const redirect = (location.state as { from?: string } | null)?.from ?? '/classes'
      navigate(redirect, { replace: true })
    },
    onError: (err) => {
      const norm = extractApiError(err)
      setSubmitError(norm.message)
    },
  })

  if (user) return <Navigate to="/classes" replace />

  return (
    <div className="min-h-screen flex">
      {/* Painel esquerdo — form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-bg-app">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <SophiaWordmark />
          </div>

          <h1 className="font-display text-2xl font-bold text-fg-1 mb-1.5">Entrar no Studio</h1>
          <p className="text-sm text-fg-3 mb-8">
            Edite classes, rode benchmarks, audite classificações.
          </p>

          <form
            onSubmit={handleSubmit((data) => {
              setSubmitError(null)
              mutation.mutate(data)
            })}
            className="flex flex-col gap-5"
            noValidate
          >
            <Field label="Email" htmlFor="email" required error={errors.email?.message}>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                autoFocus
                invalid={!!errors.email}
                {...register('email')}
              />
            </Field>

            <Field label="Senha" htmlFor="senha" required error={errors.senha?.message}>
              <Input
                id="senha"
                type="password"
                autoComplete="current-password"
                invalid={!!errors.senha}
                {...register('senha')}
              />
            </Field>

            {submitError && (
              <div className="text-sm text-danger bg-danger-bg border border-danger/30 rounded-md px-3 py-2">
                {submitError}
              </div>
            )}

            <Button type="submit" loading={mutation.isPending} className="mt-2">
              Entrar
            </Button>
          </form>

          <p className="text-xs text-fg-3 mt-8">
            Acesso interno. Sem cadastro próprio — fale com o admin pra criar usuário.
          </p>
        </div>
      </div>

      {/* Painel direito — brand */}
      <div
        className="hidden lg:flex flex-1 items-center justify-center p-12 relative overflow-hidden"
        style={{
          background:
            'radial-gradient(120% 80% at 30% 20%, var(--brand-700) 0%, var(--brand-900) 60%, var(--brand-950) 100%)',
        }}
      >
        <div className="relative z-10 max-w-md text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-pill bg-white/10 backdrop-blur-sm mb-8 border border-white/15">
            <span className="data-mono text-2xs uppercase tracking-wider text-accent-300">Studio Interno</span>
          </div>
          <h2 className="font-display text-3xl font-bold text-white leading-tight mb-4">
            Classes são dados.
            <br />
            Não código.
          </h2>
          <p className="text-base text-white/70 leading-relaxed">
            Edite regex, rode benchmark contra um gabarito, promova pra produção. Sem deploy, sem ticket.
          </p>
        </div>
      </div>
    </div>
  )
}
